import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Shield, Database, Lock, FileSearch } from 'lucide-react';
import { trackSectionView } from '../utils/analytics';

const trustPoints = [
  {
    icon: Shield,
    title: 'Privacy first',
    description:
      'Your product decisions are yours alone. We never train AI models on your briefs or strategy data.',
  },
  {
    icon: Database,
    title: 'Zero data monetization',
    description:
      "We don't sell or share your data. Our business model is based on value, not your information.",
  },
  {
    icon: Lock,
    title: 'Encrypted by default',
    description:
      "AES-256 at rest, TLS 1.3 in transit. Your data is protected whether you're a solo builder or a team.",
  },
  {
    icon: FileSearch,
    title: 'Full decision traceability',
    description:
      'Every decision is logged with its original context, assumptions, and confidence scores.',
  },
];

export default function TrustSection() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            trackSectionView('security');
          }
        });
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="security"
      ref={sectionRef}
      className="py-32 bg-brand-50 relative overflow-hidden"
    >
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-[13px] font-bold text-brand-900 uppercase tracking-[0.3em] mb-6">
            Your data
          </h2>
          <h3 className="text-4xl md:text-6xl font-bold tracking-tight text-brand-900 mb-8">
            Your plans stay yours. Always.
          </h3>
          <p className="text-xl text-brand-600 font-light leading-relaxed max-w-2xl mx-auto">
            Your product decisions, briefs, and ideas stay private. We never train on your data,
            never sell it, and never share it.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border border-brand-100 rounded-[2.5rem] overflow-hidden bg-white max-w-7xl mx-auto divide-y md:divide-y-0 lg:divide-x divide-brand-100">
          {trustPoints.map((point, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="group p-8 md:p-10 hover:bg-brand-50/50 transition-colors duration-500 text-left"
            >
              <point.icon
                className="w-10 h-10 text-brand-900 mb-6 group-hover:text-brand-600 transition-colors duration-300"
                strokeWidth={1.5}
              />
              <h4 className="text-lg font-bold text-brand-900 mb-4">{point.title}</h4>
              <p className="text-sm text-brand-500 leading-relaxed">{point.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
